import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { ASSET_TYPE_META, parseJsonArray } from './assetShared';

const API_BASE = '/api/asset-drafts';

// Yeni Demirbaş Talep/Onay — saha temsilcisinin bildirdiği yeni demirbaş taslakları.
// NOT: /api/asset-drafts REST (OData değil) — ApiResponse<{ items, pendingCount }>. useC360List
// kullanılamaz; sunucu approvalStatus ile filtreler, c360 görünümü istemci tarafında uygulanır.
// Onay Asset'e materialize eder (backend); Ret taslağı reddeder — gövdesiz POST.

// approvalStatus sayısaldır: 0 Beklemede | 1 Onaylı | 2 Reddedildi.
const APPROVAL_TABS = [
  { key: '', label: 'Tümü' },
  { key: '0', label: 'Beklemede' },
  { key: '1', label: 'Onaylı' },
  { key: '2', label: 'Reddedildi' },
];
const APPROVAL_META = {
  0: { label: 'Beklemede', tone: 'wait' },
  1: { label: 'Onaylı', tone: 'ok' },
  2: { label: 'Reddedildi', tone: 'bad' },
};

const TYPE_OPTIONS = Object.entries(ASSET_TYPE_META).map(([value, label]) => ({ value, label }));

const COLUMN_FILTER_META = {
  customerTitle: { type: 'text', odataField: 'CustomerTitle' },
  assetType: { type: 'select', odataField: 'AssetType', options: TYPE_OPTIONS },
  barcode: { type: 'text', odataField: 'Barcode' },
  serialNo: { type: 'text', odataField: 'SerialNo' },
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    pendingCount: pick(data, 'pendingCount', 'PendingCount') ?? 0,
  };
}

function approvalBadge(status) {
  const meta = APPROVAL_META[Number(status)] || { label: '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

// attributesJson → [{ label/name, value }] veya {ad: değer} olabilir; iki biçimi de listeye çevir.
function parseAttributes(raw) {
  if (!raw) return [];
  let parsed = raw;
  if (typeof raw === 'string') {
    try { parsed = JSON.parse(raw); } catch { return []; }
  }
  if (Array.isArray(parsed)) {
    return parsed.map((it) => ({
      label: pick(it, 'label', 'Label', 'name', 'Name', 'key', 'Key') || '',
      value: pick(it, 'value', 'Value') ?? '',
    })).filter((x) => x.label || x.value !== '');
  }
  if (parsed && typeof parsed === 'object') {
    return Object.entries(parsed).map(([label, value]) => ({ label, value }));
  }
  return [];
}

export function AssetDraftsPage() {
  usePageMeta('Yeni Demirbaş Onay', 'Saha temsilcilerinin bildirdiği yeni demirbaş taslakları — onay/ret.');
  const { canEdit } = usePageActions();

  const [approvalFilter, setApprovalFilter] = useState('0');

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});

  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('ReportedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (approvalFilter !== '') params.set('approvalStatus', approvalFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setPendingCount(data.pendingCount);
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [approvalFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  // Detay: GET /api/asset-drafts/{id} — foto/özellikleri tam çek.
  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    const id = pick(row, 'id', 'Id');
    if (!id) return;
    try {
      const res = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
      if (!res.ok) return;
      const full = pick(await res.json(), 'Data', 'data');
      if (full) setDetailRow((cur) => (cur && String(pick(cur, 'id', 'Id')) === String(id) ? { ...cur, ...full } : cur));
    } catch { /* sessiz: liste satırındaki veri yeterli */ }
  }, []);

  // Onay / Ret — gövdesiz POST; başarıda listeyi tazele.
  const decide = useCallback(async (row, action) => {
    const id = pick(row, 'id', 'Id');
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/${action}`, { method: 'POST', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      showToast('success', action === 'approve' ? 'Talep onaylandı — demirbaş oluşturuldu.' : 'Talep reddedildi.');
      setDetailRow(null);
      load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setActionBusyId(null);
    }
  }, [load, showToast]);

  const applyFilterText = (text) => { setFilterText(text); setPage(1); };
  const setColumnFilter = (columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setPage(1);
  };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setPage(1);
  };

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const textMatch = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'customerTitle', 'CustomerTitle'),
        pick(row, 'barcode', 'Barcode'),
        pick(row, 'serialNo', 'SerialNo'),
        pick(row, 'brand', 'Brand'),
        pick(row, 'modelName', 'ModelName'),
      ];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const colMatch = (row) => {
      for (const [columnId, filter] of Object.entries(columnFilters)) {
        if (!filter) continue;
        const meta = COLUMN_FILTER_META[columnId];
        if (!meta) continue;
        const cap = columnId.charAt(0).toUpperCase() + columnId.slice(1);
        if (meta.type === 'select') {
          const v = filter.value;
          if (v === '' || v == null) continue;
          if (String(pick(row, columnId, cap) ?? '') !== String(v)) return false;
        } else if (meta.type === 'text') {
          const v = (filter.value ?? '').toString().toLocaleLowerCase('tr-TR');
          if (!v) continue;
          const cell = String(pick(row, columnId, cap) ?? '').toLocaleLowerCase('tr-TR');
          if (!cell.includes(v)) return false;
        }
      }
      return true;
    };
    const filtered = rows.filter((r) => textMatch(r) && colMatch(r));
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'CustomerTitle': return pick(row, 'customerTitle', 'CustomerTitle') || '';
        case 'AssetType': return pick(row, 'assetType', 'AssetType') || '';
        case 'Barcode': return pick(row, 'barcode', 'Barcode') || '';
        case 'SerialNo': return pick(row, 'serialNo', 'SerialNo') || '';
        case 'ApprovalStatus': return Number(pick(row, 'approvalStatus', 'ApprovalStatus') ?? 0);
        case 'ReportedAt':
        default: {
          const v = pick(row, 'reportedAt', 'ReportedAt');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('reportedAt', {
      header: 'Bildirim Tarihi', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('customerTitle', {
      header: 'Müşteri', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('assetType', {
      header: 'Tip', meta: { filterType: 'select' },
      cell: (info) => ASSET_TYPE_META[info.getValue()] || info.getValue() || '-',
    }),
    columnHelper.accessor('barcode', {
      header: 'Barkod / Seri', meta: { filterType: 'text' },
      cell: ({ row }) => {
        const bc = pick(row.original, 'barcode', 'Barcode');
        const sn = pick(row.original, 'serialNo', 'SerialNo');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            {bc ? <span className="c360-erp-chip">{bc}</span> : '-'}
            {sn && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>Seri: {sn}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('modelName', {
      header: 'Marka / Model', enableColumnFilter: false,
      cell: ({ row }) => {
        const brand = pick(row.original, 'brand', 'Brand');
        const modelName = pick(row.original, 'modelName', 'ModelName');
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 600 }}>{brand || modelName || '-'}</span>
            {(modelName && brand) && <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)' }}>{modelName}</span>}
          </div>
        );
      },
    }),
    columnHelper.accessor('approvalStatus', {
      header: 'Onay Durumu', enableColumnFilter: false,
      cell: (info) => approvalBadge(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => {
        const rid = pick(row.original, 'id', 'Id');
        const status = Number(pick(row.original, 'approvalStatus', 'ApprovalStatus') ?? 0);
        const busy = actionBusyId === rid;
        return (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
            {canEdit && status !== 1 && (
              <button type="button" style={approveBtnStyle} title="Onayla" aria-label="Onayla" disabled={busy} onClick={(e) => { e.stopPropagation(); decide(row.original, 'approve'); }}>Onayla</button>
            )}
            {canEdit && status !== 2 && (
              <button type="button" style={rejectBtnStyle} title="Reddet" aria-label="Reddet" disabled={busy} onClick={(e) => { e.stopPropagation(); decide(row.original, 'reject'); }}>Reddet</button>
            )}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, actionBusyId, decide, openDetail]);

  const gridToOData = {
    reportedAt: 'ReportedAt',
    customerTitle: 'CustomerTitle',
    assetType: 'AssetType',
    barcode: 'Barcode',
    approvalStatus: 'ApprovalStatus',
  };
  const odataToGrid = Object.fromEntries(Object.entries(gridToOData).map(([k, v]) => [v, k]));
  const sorting = useMemo(() => {
    const gridId = odataToGrid[sortField];
    return gridId ? [{ id: gridId, desc: sortDir === 'desc' }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    setPage(1);
    if (!first) { setSortField('ReportedAt'); setSortDir('desc'); return; }
    setSortField(gridToOData[first.id] || 'ReportedAt');
    setSortDir(first.desc ? 'desc' : 'asc');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: page - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    try {
      const cols = [
        { headerName: 'Bildirim Tarihi', valueGetter: ({ row }) => formatDate(pick(row, 'reportedAt', 'ReportedAt')) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => pick(row, 'customerTitle', 'CustomerTitle') || '' },
        { headerName: 'Tip', valueGetter: ({ row }) => (ASSET_TYPE_META[pick(row, 'assetType', 'AssetType')] || pick(row, 'assetType', 'AssetType') || '') },
        { headerName: 'Barkod', valueGetter: ({ row }) => pick(row, 'barcode', 'Barcode') || '' },
        { headerName: 'Seri No', valueGetter: ({ row }) => pick(row, 'serialNo', 'SerialNo') || '' },
        { headerName: 'Marka', valueGetter: ({ row }) => pick(row, 'brand', 'Brand') || '' },
        { headerName: 'Model', valueGetter: ({ row }) => pick(row, 'modelName', 'ModelName') || '' },
        { headerName: 'Onay Durumu', valueGetter: ({ row }) => (APPROVAL_META[Number(pick(row, 'approvalStatus', 'ApprovalStatus') ?? 0)]?.label || '') },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `demirbas-talepleri_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {APPROVAL_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setApprovalFilter(tab.key)}
          style={{
            ...(approvalFilter === tab.key ? s.btnPrimary : s.btnSecondary),
            padding: '6px 14px', fontSize: 13,
          }}
        >
          {tab.label}
        </button>
      ))}
      <input
        style={s.filterInput}
        type="text"
        placeholder="Müşteri / barkod / seri / marka..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={page}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
    />
  );

  const detailPhotos = detailRow ? parseJsonArray(pick(detailRow, 'photoUrlsJson', 'PhotoUrlsJson')) : [];
  const detailAttrs = detailRow ? parseAttributes(pick(detailRow, 'attributesJson', 'AttributesJson')) : [];
  const detailStatus = detailRow ? Number(pick(detailRow, 'approvalStatus', 'ApprovalStatus') ?? 0) : 0;
  const detailId = detailRow ? pick(detailRow, 'id', 'Id') : null;
  const detailBusy = detailId != null && actionBusyId === detailId;

  return (
    <div style={s.container}>
      <ListPageShell
        title="Yeni Demirbaş Onay"
        subtitle="Saha temsilcilerinin bildirdiği yeni demirbaş talepleri. Onaylanan talep otomatik olarak demirbaş envanterine eklenir; reddedilen talep kapatılır."
        meta={(
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '7px 12px', borderRadius: 999,
              background: 'rgba(253, 171, 61, 0.14)', border: '1px solid rgba(253, 171, 61, 0.32)',
              color: '#92400e', fontWeight: 800, fontSize: 12,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--warn)' }} />
              Bekleyen Talep: {pendingCount}
            </span>
          </div>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={(row) => openDetail(row)}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {pick(detailRow, 'customerTitle', 'CustomerTitle') || 'Demirbaş Talebi'}
                {pick(detailRow, 'barcode', 'Barcode') ? ` • ${pick(detailRow, 'barcode', 'Barcode')}` : ''}
              </h3>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Onay Durumu</label>
                  <div>{approvalBadge(detailStatus)}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Demirbaş Tipi</label>
                  <div style={s.inputView}>{ASSET_TYPE_META[pick(detailRow, 'assetType', 'AssetType')] || pick(detailRow, 'assetType', 'AssetType') || '-'}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Barkod</label>
                  <div style={s.inputView}>{pick(detailRow, 'barcode', 'Barcode') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Seri No</label>
                  <div style={s.inputView}>{pick(detailRow, 'serialNo', 'SerialNo') || '-'}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Marka</label>
                  <div style={s.inputView}>{pick(detailRow, 'brand', 'Brand') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Model</label>
                  <div style={s.inputView}>{pick(detailRow, 'modelName', 'ModelName') || '-'}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Bildiren</label>
                  <div style={s.inputView}>{pick(detailRow, 'proposedBy', 'ProposedBy') || '-'}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bildirim Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'reportedAt', 'ReportedAt'))}</div>
                </div>
              </div>
              {pick(detailRow, 'note', 'Note') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Not</label>
                  <div style={s.inputView}>{pick(detailRow, 'note', 'Note')}</div>
                </div>
              )}
              {detailAttrs.length > 0 && (
                <div style={s.formGroup}>
                  <label style={s.label}>Özellikler</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {detailAttrs.map((a, idx) => (
                      <span key={idx} style={{
                        display: 'inline-flex', gap: 6, padding: '4px 10px', borderRadius: 8,
                        background: 'var(--bg-subtle, #f3f4f6)', border: '1px solid var(--border-color, #e5e7eb)', fontSize: 12,
                      }}>
                        <strong>{a.label}:</strong> {String(a.value ?? '')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {detailPhotos.length > 0 && (
                <div style={s.formGroup}>
                  <label style={s.label}>Fotoğraflar ({detailPhotos.length})</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {detailPhotos.map((url, idx) => (
                      <a key={idx} href={url} target="_blank" rel="noreferrer"
                         style={{ display: 'block', width: 120, height: 120, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                        <img src={url} alt={`Talep ${idx + 1}`}
                             style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                             onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              {canEdit && detailStatus !== 2 && (
                <button type="button" style={{ ...s.btnDanger, opacity: detailBusy ? 0.6 : 1 }} disabled={detailBusy} onClick={() => decide(detailRow, 'reject')}>{detailBusy ? '...' : 'Reddet'}</button>
              )}
              {canEdit && detailStatus !== 1 && (
                <button type="button" style={{ ...s.btnPrimary, opacity: detailBusy ? 0.6 : 1 }} disabled={detailBusy} onClick={() => decide(detailRow, 'approve')}>{detailBusy ? '...' : 'Onayla'}</button>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

// Onay/Ret satır butonları — dps-btn .dps kökü dışında görünmez olduğu için inline stil.
const approveBtnStyle = {
  padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(22, 163, 74, 0.4)',
  background: 'rgba(0, 200, 117, 0.12)', color: '#15803d', fontWeight: 700, fontSize: 12, cursor: 'pointer',
};
const rejectBtnStyle = {
  padding: '4px 10px', borderRadius: 8, border: '1px solid rgba(220, 38, 38, 0.4)',
  background: 'rgba(226, 68, 92, 0.10)', color: '#b91c1c', fontWeight: 700, fontSize: 12, cursor: 'pointer',
};

export default AssetDraftsPage;
